import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Trash2, BarChart3, AlertTriangle, TrendingUp, Loader2, ChevronDown, ChevronRight,
  Target, DollarSign, Search, Layers, Zap, Plus, FileText, CheckCircle, Info, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { apiGet, apiDelete, authFetch, isPostMessageNoise } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADE_COLORS = {
  'A': { bg: 'bg-emerald-500', text: 'text-emerald-600', ring: 'ring-emerald-200', fill: '#10b981' },
  'A-': { bg: 'bg-emerald-400', text: 'text-emerald-500', ring: 'ring-emerald-200', fill: '#34d399' },
  'B': { bg: 'bg-blue-500', text: 'text-blue-600', ring: 'ring-blue-200', fill: '#3b82f6' },
  'B-': { bg: 'bg-blue-400', text: 'text-blue-500', ring: 'ring-blue-200', fill: '#60a5fa' },
  'C': { bg: 'bg-amber-500', text: 'text-amber-600', ring: 'ring-amber-200', fill: '#f59e0b' },
  'C-': { bg: 'bg-amber-400', text: 'text-amber-500', ring: 'ring-amber-200', fill: '#fbbf24' },
  'D': { bg: 'bg-orange-500', text: 'text-orange-600', ring: 'ring-orange-200', fill: '#f97316' },
  'F': { bg: 'bg-red-500', text: 'text-red-600', ring: 'ring-red-200', fill: '#ef4444' },
};

const CATEGORY_META = {
  account_structure: { label: 'Account Structure', icon: Layers, desc: 'Campaign organization, naming, segmentation' },
  campaign_settings: { label: 'Campaign Settings', icon: Target, desc: 'Bidding, budgets, conversion tracking' },
  keyword_health: { label: 'Keyword Health', icon: Search, desc: 'Match types, Quality Score, performance' },
  ad_performance: { label: 'Ad Performance', icon: TrendingUp, desc: 'CTR, CPC, conversion efficiency' },
  budget_efficiency: { label: 'Budget Efficiency', icon: DollarSign, desc: 'Spend distribution, waste analysis' },
  search_term_quality: { label: 'Search Term Quality', icon: Zap, desc: 'Query relevance, negative coverage' },
};

const PRIORITY_STYLES = {
  Critical: 'bg-red-50 border-red-200 text-red-800',
  High: 'bg-amber-50 border-amber-200 text-amber-800',
  Medium: 'bg-blue-50 border-blue-200 text-blue-800',
  Low: 'bg-gray-50 border-gray-200 text-gray-600',
};

const SEVERITY_STYLES = {
  critical: { wrap: 'border-red-300 bg-red-50/40', badge: 'bg-red-600 text-white', label: 'CRITICAL' },
  high: { wrap: 'border-amber-300 bg-amber-50/40', badge: 'bg-amber-600 text-white', label: 'HIGH' },
  medium: { wrap: 'border-blue-300 bg-blue-50/40', badge: 'bg-blue-600 text-white', label: 'MEDIUM' },
  low: { wrap: 'border-gray-300 bg-gray-50/40', badge: 'bg-gray-500 text-white', label: 'LOW' },
  info: { wrap: 'border-slate-200 bg-slate-50/40', badge: 'bg-slate-400 text-white', label: 'INFO' },
};

const ROLLBACK_STYLES = {
  low: 'text-emerald-700 bg-emerald-50',
  medium: 'text-amber-700 bg-amber-50',
  high: 'text-red-700 bg-red-50',
};

// ═══════════════ STRUCTURED REC CARD (PMax audits) ═══════════════
const StructuredRecCard = ({ rec, index }) => {
  const sev = SEVERITY_STYLES[rec.severity] || SEVERITY_STYLES.medium;
  return (
    <div
      className={`rounded-lg border-2 ${sev.wrap} p-5 space-y-3`}
      data-testid={`structured-rec-${index}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${sev.badge}`}>
            {sev.label}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-white text-text-secondary border border-border-subtle">
            {rec.domain_label || rec.domain || 'General'}
          </span>
          <span className="text-[10px] font-medium text-text-tertiary">
            Confidence: <span className="font-bold text-text-primary">{Math.round((rec.confidence || 0) * 100)}%</span>
          </span>
        </div>
        <span className="text-xs font-semibold text-text-tertiary">#{index + 1}</span>
      </div>

      <h4 className="text-base font-bold text-text-primary leading-tight" data-testid={`rec-issue-${index}`}>
        {rec.issue}
      </h4>

      {rec.evidence?.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted mb-1.5">Evidence</p>
          <ul className="space-y-1">
            {rec.evidence.map((e, i) => (
              <li key={`ev-${index}-${i}`} className="text-xs text-text-secondary flex items-start gap-2">
                <Info size={12} className="mt-0.5 flex-shrink-0 text-text-tertiary" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted mb-1.5">Recommendation</p>
        <p className="text-sm text-text-primary leading-relaxed" data-testid={`rec-text-${index}`}>{rec.recommendation}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border-subtle">
        {rec.estimated_impact && (
          <div className="flex items-center gap-1.5 text-xs">
            <TrendingUp size={12} className="text-emerald-600" />
            <span className="text-text-tertiary">Impact:</span>
            <span className="font-semibold text-text-primary">{rec.estimated_impact}</span>
          </div>
        )}
        {rec.rollback_risk && (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-text-tertiary">Rollback risk:</span>
            <span className={`font-semibold px-1.5 py-0.5 rounded ${ROLLBACK_STYLES[rec.rollback_risk] || 'text-gray-700 bg-gray-50'}`}>
              {rec.rollback_risk}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════ SCORE RING ═══════════════
const ScoreRing = ({ score, max, size = 80, strokeWidth = 6 }) => {
  const pct = max > 0 ? (score / max) * 100 : 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const color = pct >= 70 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        className="transition-all duration-1000 ease-out"
      />
    </svg>
  );
};

// ═══════════════ CATEGORY CARD ═══════════════
const CategoryCard = ({ catKey, data }) => {
  const [expanded, setExpanded] = useState(false);
  const fallback = CATEGORY_META[catKey] || { label: data.label || catKey, icon: BarChart3, desc: '' };
  const meta = { ...fallback, label: data.label || fallback.label };
  const Icon = meta.icon;
  const pct = data.max_score > 0 ? (data.score / data.max_score) * 100 : 0;
  // PMax: domains that aren't Tier 1 are marked tier_2_pending with max_score=0; render distinctly.
  const isPending = data.tier === 'tier_2_pending' || data.max_score === 0;

  return (
    <Card className="overflow-hidden" data-testid={`category-${catKey}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-surface-page/50 transition-colors"
        data-testid={`category-toggle-${catKey}`}
      >
        <div className="relative w-14 h-14 flex-shrink-0">
          {isPending ? (
            <div className="w-14 h-14 rounded-full border-2 border-dashed border-purple-300 flex items-center justify-center bg-purple-50/30">
              <span className="text-[10px] font-semibold text-purple-600">T2</span>
            </div>
          ) : (
            <>
              <ScoreRing score={data.score} max={data.max_score} size={56} strokeWidth={5} />
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-text-primary">
                {data.score}/{data.max_score}
              </span>
            </>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={16} className="text-brand" />
            <h3 className="font-semibold text-text-primary">{meta.label}</h3>
            {isPending && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Tier 2 pending</span>
            )}
          </div>
          <p className="text-xs text-text-tertiary mt-0.5">{meta.desc}</p>
        </div>
        <div className="flex items-center gap-3">
          {!isPending && (
            <span className={`text-sm font-bold ${pct >= 70 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
              {pct.toFixed(0)}%
            </span>
          )}
          {expanded ? <ChevronDown size={16} className="text-text-muted" /> : <ChevronRight size={16} className="text-text-muted" />}
        </div>
      </button>

      {expanded && (
        <CardContent className="pt-0 px-5 pb-5 border-t border-border-subtle">
          {data.findings?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">Findings</p>
              <ul className="space-y-1.5">
                {data.findings.map((f, i) => (
                  <li key={`f-${i}-${f.slice(0,20)}`} className="flex items-start gap-2 text-sm text-text-secondary">
                    <Info size={14} className="mt-0.5 flex-shrink-0 text-text-tertiary" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.recommendations?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">Recommendations</p>
              <ul className="space-y-1.5">
                {data.recommendations.map((r, i) => {
                  const isStructured = typeof r === 'object' && r !== null;
                  const text = isStructured ? r.recommendation : r;
                  const sev = isStructured ? (r.severity || '').toUpperCase() : '';
                  const keyText = isStructured ? (r.issue || r.recommendation || '') : r;
                  return (
                    <li key={`r-${i}-${(keyText || '').slice(0,20)}`} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded px-2.5 py-1.5">
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                      <span>
                        {sev && <span className="text-[10px] font-bold mr-1.5 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">{sev}</span>}
                        {text}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ═══════════════ AUDIT DETAIL VIEW ═══════════════
const AuditDetail = ({ audit, onBack, onDelete }) => {
  const gc = GRADE_COLORS[audit.grade] || GRADE_COLORS['C'];
  const categories = audit.categories || {};
  const recs = audit.recommendations || [];
  const summary = audit.data_summary || {};
  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      // Async PDF flow — production-safe (avoids 30s proxy timeout):
      //   1. POST /prepare-pdf  → returns pdf_job_id immediately
      //   2. Poll /pdf-job/{id} until status=ready or failed
      //   3. Navigate to /api/download/{download_id} (no-auth direct download)
      const prepRes = await authFetch(`${API}/api/audits/${audit.id}/prepare-pdf`, { method: 'POST' });
      if (!prepRes.ok) {
        const err = await prepRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to start PDF generation');
      }
      const { pdf_job_id } = await prepRes.json();

      // Poll every 2s for up to 3 minutes
      const MAX_TRIES = 90;
      let job = null;
      for (let i = 0; i < MAX_TRIES; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const jobRes = await authFetch(`${API}/api/audits/pdf-job/${pdf_job_id}`);
        if (!jobRes.ok) {
          throw new Error('PDF job lookup failed');
        }
        job = await jobRes.json();
        if (job.status === 'ready' || job.status === 'failed') break;
      }

      if (!job || job.status !== 'ready') {
        throw new Error(job?.error || 'PDF generation timed out — please try again');
      }

      // Navigate to the signed download URL (HMAC + 5-min TTL — replaces the
      // previous naked-UUID pattern that allowed unauth users to retrieve files)
      window.location.href = `${API}${job.download_url}`;
      toast.success('PDF ready');
    } catch (e) {
      if (!isPostMessageNoise(e)) {
        toast.error(e.message);
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="audit-back-btn">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-text-primary">{audit.name}</h2>
              {audit.scope && <ScopeBadge scope={audit.scope} />}
            </div>
            <p className="text-xs text-text-tertiary mt-0.5">
              Created {new Date(audit.created_at).toLocaleDateString()} by {audit.created_by_name}
              {audit.data_source_meta?.account_name && ` · ${audit.data_source_meta.account_name}`}
              {audit.data_source_meta?.date_range && ` · ${audit.data_source_meta.date_range.replace(/_/g, ' ').toLowerCase()}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exporting} data-testid="audit-export-pdf-btn">
            {exporting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}
            {exporting ? 'Generating PDF...' : 'Export PDF'}
          </Button>
          <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(audit.id)} data-testid="audit-delete-btn">
            <Trash2 size={14} className="mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* Score Header */}
      {audit.audit_format !== 'pmax_structured_v1' && (summary.campaigns_analyzed === 0 && summary.keywords_analyzed === 0 && summary.search_terms_analyzed === 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="parse-warning">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">CSV data was not recognized</p>
            <p className="text-xs text-amber-700 mt-1">
              The uploaded files could not be parsed. This can happen if the CSV format is unexpected.
              Try re-exporting from Google Ads and uploading again. Delete this audit and create a new one.
            </p>
            {audit.uploaded_files?.some(f => f.raw_headers?.length > 0) && (
              <div className="mt-2">
                <p className="text-xs font-medium text-amber-700">Detected headers:</p>
                {audit.uploaded_files.map((f, i) => (
                  <p key={f.filename || i} className="text-[11px] text-amber-600 mt-0.5">
                    {f.filename}: {f.raw_headers?.slice(0, 6).join(', ') || 'none detected'}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Card className="overflow-hidden" data-testid="audit-score-header">
        <div className="flex flex-col md:flex-row">
          <div className={`${gc.bg} text-white px-8 py-6 flex flex-col items-center justify-center min-w-[180px]`}>
            <span className="text-6xl font-black leading-none">{audit.grade}</span>
            <span className="text-sm font-medium mt-2 opacity-90">Overall Grade</span>
          </div>
          <div className="flex-1 p-6">
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-3xl font-bold text-text-primary">{audit.percentage}%</span>
              <span className="text-sm text-text-tertiary">({audit.total_score} / {audit.max_score} points)</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ease-out ${gc.bg}`}
                style={{ width: `${audit.percentage}%` }}
              />
            </div>
            <div className="flex gap-6 mt-4 text-sm flex-wrap">
              <div data-testid="summary-campaigns">
                <span className="text-text-tertiary">Campaigns: </span>
                <span className="font-semibold text-text-primary">{summary.campaigns_analyzed || 0}</span>
              </div>
              {audit.audit_format === 'pmax_structured_v1' ? (
                <>
                  <div data-testid="summary-asset-groups">
                    <span className="text-text-tertiary">Asset Groups: </span>
                    <span className="font-semibold text-text-primary">{summary.asset_groups_analyzed || 0}</span>
                  </div>
                  <div data-testid="summary-signals">
                    <span className="text-text-tertiary">Asset Group Signals: </span>
                    <span className="font-semibold text-text-primary">{summary.asset_group_signals_analyzed || 0}</span>
                  </div>
                  {summary.tier_1_complete && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-purple-100 text-purple-700 self-center">Tier 1 Complete</span>
                  )}
                </>
              ) : (
                <>
                  <div data-testid="summary-keywords">
                    <span className="text-text-tertiary">Keywords: </span>
                    <span className="font-semibold text-text-primary">{summary.keywords_analyzed || 0}</span>
                  </div>
                  <div data-testid="summary-search-terms">
                    <span className="text-text-tertiary">Search Terms: </span>
                    <span className="font-semibold text-text-primary">{summary.search_terms_analyzed || 0}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Suggested columns for better scoring */}
      {summary.suggested_export_columns?.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4" data-testid="suggested-columns">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Add these columns to your next export for a more complete audit:</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {summary.suggested_export_columns.map((col) => (
                  <span key={col} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">{col}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">Audit Categories</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Object.entries(categories).map(([key, data]) => (
            <CategoryCard key={key} catKey={key} data={data} />
          ))}
        </div>
      </div>

      {/* Recommendations */}
      {recs.length > 0 && (
        <div data-testid="audit-recommendations">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">
            All Recommendations ({recs.length})
          </h3>
          {audit.audit_format === 'pmax_structured_v1' ? (
            <div className="space-y-3" data-testid="pmax-structured-recs">
              {recs.map((r, i) => (
                <StructuredRecCard key={`pmax-rec-${i}-${(r.issue || '').slice(0,20)}`} rec={r} index={i} />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {recs.map((r, i) => (
                <div
                  key={`rec-${r.category}-${i}`}
                  className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${PRIORITY_STYLES[r.priority] || PRIORITY_STYLES.Medium}`}
                  data-testid={`recommendation-${i}`}
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-white/60 flex-shrink-0 mt-0.5">
                    {r.priority}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{r.recommendation}</p>
                    <p className="text-[11px] opacity-60 mt-0.5">{CATEGORY_META[r.category]?.label || r.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Uploaded Files */}
      {audit.uploaded_files?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Uploaded Files</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {audit.uploaded_files.map((f, i) => (
                <div key={f.filename || i} className="flex items-center gap-3 text-sm p-2 bg-surface-page rounded" data-testid={`uploaded-file-${i}`}>
                  <FileText size={16} className="text-brand" />
                  <span className="font-medium text-text-primary">{f.filename}</span>
                  <span className="text-text-tertiary">|</span>
                  <span className="text-text-secondary capitalize">{f.report_type}</span>
                  <span className="text-text-tertiary">|</span>
                  <span className="text-text-tertiary">{f.row_count} rows</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ═══════════════ SCOPE BADGES ═══════════════
const SCOPE_BADGES = {
  search: { label: 'Search', class: 'bg-blue-100 text-blue-700' },
  youtube: { label: 'YouTube', class: 'bg-red-100 text-red-700' },
  pmax: { label: 'PMax', class: 'bg-purple-100 text-purple-700' },
  display: { label: 'Display', class: 'bg-amber-100 text-amber-700' },
  shopping: { label: 'Shopping', class: 'bg-green-100 text-green-700' },
  all: { label: 'All Channels', class: 'bg-gray-100 text-gray-700' },
};

const ScopeBadge = ({ scope }) => {
  const s = SCOPE_BADGES[scope] || SCOPE_BADGES.search;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.class}`} data-testid={`scope-badge-${scope}`}>
      {s.label}
    </span>
  );
};

// ═══════════════ CREATE AUDIT FORM ═══════════════
const CreateAuditForm = ({ projects, onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [running, setRunning] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [accountQuery, setAccountQuery] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [dateRange, setDateRange] = useState('LAST_30_DAYS');
  const [scope, setScope] = useState('search');
  const [accountsLoading, setAccountsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/optimization/accounts');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        // Exclude demo accounts — API audits need real data
        const real = (data.accounts || []).filter((a) => !a.is_demo);
        setAccounts(real);
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredAccounts = accountQuery.trim()
    ? accounts.filter((a) => {
        const q = accountQuery.trim().toLowerCase();
        return (a.name || '').toLowerCase().includes(q) || (a.customer_id || '').includes(q);
      })
    : accounts;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Audit name is required'); return; }
    if (!selectedAccountId) { toast.error('Choose a Google Ads account'); return; }

    setRunning(true);
    try {
      const res = await authFetch(`${API}/api/audits/from-google-ads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          project_id: projectId || null,
          account_id: selectedAccountId,
          date_range: dateRange,
          scope,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create audit');
      }
      const audit = await res.json();
      toast.success(`Audit complete — Grade: ${audit.grade} (${audit.percentage}%)`);
      onCreated(audit);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="max-w-2xl" data-testid="create-audit-form">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 size={20} className="text-brand" />
            New Paid Search Audit
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="audit-name">Audit Name</Label>
            <Input
              id="audit-name"
              placeholder="e.g., Q1 2025 Google Ads Audit"
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="audit-name-input"
            />
          </div>

          <div>
            <Label htmlFor="project-select">Link to Project (optional)</Label>
            <select
              id="project-select"
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
              data-testid="audit-project-select"
            >
              <option value="">No project</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Google Ads Account</Label>
            <p className="text-xs text-text-tertiary mb-2">
              Pulls campaigns, keywords, and search-term reports live via the Google Ads API.
            </p>
            {accountsLoading ? (
              <div className="py-4 text-center text-sm text-text-tertiary">
                <Loader2 size={16} className="inline mr-2 animate-spin" /> Loading accounts...
              </div>
            ) : accounts.length === 0 ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                No connected Google Ads accounts found. Connect via the Optimization Center first.
              </div>
            ) : (
              <>
                <Input
                  placeholder={`Search ${accounts.length} accounts...`}
                  value={accountQuery}
                  onChange={(e) => setAccountQuery(e.target.value)}
                  className="mb-2"
                  data-testid="audit-account-search"
                />
                <div className="max-h-48 overflow-y-auto border border-border-subtle rounded-md divide-y divide-border-subtle">
                  {filteredAccounts.length === 0 ? (
                    <p className="text-sm text-text-tertiary text-center py-4">No matches</p>
                  ) : (
                    filteredAccounts.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setSelectedAccountId(a.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-page transition-colors flex items-center justify-between ${
                          selectedAccountId === a.id ? 'bg-brand/5' : ''
                        }`}
                        data-testid={`audit-account-${a.id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-text-primary truncate">{a.name}</p>
                          <p className="text-[11px] text-text-tertiary">{a.customer_id}</p>
                        </div>
                        {selectedAccountId === a.id && <CheckCircle size={14} className="text-brand flex-shrink-0" />}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div>
            <Label htmlFor="audit-scope-select">Audit Scope</Label>
            <select
              id="audit-scope-select"
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="audit-scope"
            >
              <option value="search">Search Only (recommended)</option>
              <option value="youtube">YouTube (Video)</option>
              <option value="pmax">Performance Max</option>
              <option value="display">Display</option>
              <option value="shopping">Shopping</option>
              <option value="all">All Channels (mixed)</option>
            </select>
            <p className="text-xs text-text-tertiary mt-1">
              {scope === 'search' && 'Pulls only Search campaigns. Scoring uses Search-relevant KPIs (CTR, Quality Score, Search Impression Share).'}
              {scope === 'youtube' && 'Pulls only Video campaigns. Note: scoring rubric is currently Search-style — YouTube-specific KPIs (VTR, watch time) coming soon.'}
              {scope === 'pmax' && 'Pulls Performance Max campaigns with asset groups, search themes, audience signals, and brand exclusions. Uses the structured PMax audit framework (Tier 1).'}
              {scope === 'display' && 'Pulls only Display campaigns. Note: scoring rubric is currently Search-style — Display KPIs (Viewable CTR, Reach/Frequency) coming soon.'}
              {scope === 'shopping' && 'Pulls only Shopping campaigns.'}
              {scope === 'all' && 'Mixes all channel types. CTR and Quality Score thresholds may be unfair across channels.'}
            </p>
          </div>

          <div>
            <Label htmlFor="date-range-select">Date Range</Label>
            <select
              id="date-range-select"
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              data-testid="audit-date-range"
            >
              <option value="LAST_7_DAYS">Last 7 days</option>
              <option value="LAST_14_DAYS">Last 14 days</option>
              <option value="LAST_30_DAYS">Last 30 days</option>
              <option value="LAST_90_DAYS">Last 90 days</option>
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_QUARTER">This quarter</option>
              <option value="LAST_QUARTER">Last quarter</option>
            </select>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={running || !selectedAccountId}
            data-testid="run-audit-btn"
          >
            {running ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Pulling data and analyzing…
              </>
            ) : (
              <>
                <BarChart3 size={16} className="mr-2" />
                Run Audit
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ═══════════════ MAIN PAGE ═══════════════
const PaidSearchAudit = ({ user }) => {
  const [view, setView] = useState('list'); // list | create | detail
  const [audits, setAudits] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAudits = useCallback(async () => {
    try {
      const res = await apiGet('/api/audits');
      if (res.ok) {
        const data = await res.json();
        setAudits(data.audits || []);
      }
    } catch (e) {
      toast.error('Failed to load audits');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const res = await apiGet('/api/projects');
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (e) { console.warn('Projects load failed:', e); }
  }, []);

  useEffect(() => {
    loadAudits();
    loadProjects();
  }, [loadAudits, loadProjects]);

  // Poll for any audits currently in `Running` state — refresh the list every
  // 5s until they all complete or fail.
  useEffect(() => {
    const hasRunning = audits.some((a) => a.status === 'Running');
    if (!hasRunning) return undefined;
    const t = setInterval(() => {
      loadAudits();
    }, 5000);
    return () => clearInterval(t);
  }, [audits, loadAudits]);

  const handleCreated = (audit) => {
    setAudits(prev => [audit, ...prev]);
    if (audit.status === 'Running') {
      // Don't open the detail view yet — it's still being computed. Stay on list.
      setView('list');
      toast.info('Audit started — pulling data from Google Ads. This usually takes 30–90 seconds.');
    } else {
      setSelectedAudit(audit);
      setView('detail');
    }
  };

  const handleDelete = async (auditId) => {
    if (!window.confirm('Delete this audit?')) return;
    try {
      const res = await apiDelete(`/api/audits/${auditId}`);
      if (!res.ok) throw new Error('Delete failed');
      setAudits(prev => prev.filter(a => a.id !== auditId));
      setView('list');
      setSelectedAudit(null);
      toast.success('Audit deleted');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleCancel = async (audit) => {
    if (!window.confirm(`Cancel and remove this running audit?\n\n"${audit.name}"`)) return;
    try {
      // Mark as cancelled server-side, then delete locally so the user gets
      // immediate visual feedback even if the background task is still chugging.
      await authFetch(`${API}/api/audits/${audit.id}/cancel`, { method: 'POST' });
      const res = await apiDelete(`/api/audits/${audit.id}`);
      if (!res.ok) throw new Error('Cancel failed');
      setAudits(prev => prev.filter(a => a.id !== audit.id));
      toast.success('Audit cancelled and removed');
    } catch (e) {
      toast.error(e.message || 'Cancel failed');
    }
  };

  const openAudit = async (audit) => {
    try {
      const res = await apiGet(`/api/audits/${audit.id}`);
      if (res.ok) {
        const full = await res.json();
        setSelectedAudit(full);
        setView('detail');
      }
    } catch (e) {
      toast.error('Failed to load audit');
    }
  };

  if (view === 'create') {
    return (
      <CreateAuditForm
        projects={projects}
        onCreated={handleCreated}
        onCancel={() => setView('list')}
      />
    );
  }

  if (view === 'detail' && selectedAudit) {
    return (
      <AuditDetail
        audit={selectedAudit}
        onBack={() => { setView('list'); setSelectedAudit(null); }}
        onDelete={handleDelete}
      />
    );
  }

  // ═══════════════ LIST VIEW ═══════════════
  return (
    <div className="space-y-6" data-testid="audit-list-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Paid Search Audits</h1>
          <p className="text-sm text-text-tertiary mt-1">
            Pull live Google Ads data via the API and score account health with actionable recommendations
          </p>
        </div>
        <Button onClick={() => setView('create')} data-testid="new-audit-btn">
          <Plus size={16} className="mr-2" /> New Audit
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand" />
        </div>
      ) : audits.length === 0 ? (
        <Card className="py-16 text-center" data-testid="empty-audits">
          <BarChart3 size={40} className="mx-auto text-text-muted mb-3" />
          <h3 className="text-lg font-semibold text-text-primary mb-1">No audits yet</h3>
          <p className="text-sm text-text-tertiary mb-4">
            Run your first Paid Search audit by selecting a connected Google Ads account
          </p>
          <Button onClick={() => setView('create')} data-testid="empty-new-audit-btn">
            <Plus size={16} className="mr-2" /> Create Audit
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {audits.map(audit => {
            const isRunning = audit.status === 'Running';
            const isFailed = audit.status === 'Failed' || audit.status === 'Cancelled';
            const gc = GRADE_COLORS[audit.grade] || GRADE_COLORS['C'];
            const project = projects.find(p => p.id === audit.project_id);
            // Compute elapsed minutes for Running audits so the user has visibility
            let elapsedMin = 0;
            if (isRunning && audit.created_at) {
              elapsedMin = Math.floor((Date.now() - new Date(audit.created_at).getTime()) / 60000);
            }
            return (
              <Card
                key={audit.id}
                className={`cursor-pointer hover:shadow-md transition-shadow group ${isRunning ? 'border-brand/40' : ''} ${isFailed ? 'border-red-200' : ''}`}
                onClick={() => isRunning ? toast.info('Audit still running — give it a minute.') : openAudit(audit)}
                data-testid={`audit-card-${audit.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl ${isRunning ? 'bg-brand/10' : isFailed ? 'bg-red-50' : gc.bg} flex items-center justify-center flex-shrink-0`}>
                      {isRunning ? (
                        <Loader2 size={22} className="animate-spin text-brand" />
                      ) : isFailed ? (
                        <AlertTriangle size={22} className="text-red-500" />
                      ) : (
                        <span className="text-white text-xl font-black">{audit.grade}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold truncate group-hover:text-brand transition-colors ${isFailed ? 'text-red-700' : 'text-text-primary'}`}>
                          {audit.name}
                        </h3>
                        {audit.scope && <ScopeBadge scope={audit.scope} />}
                      </div>
                      {project && (
                        <p className="text-xs text-brand/70 mt-0.5">{project.name}</p>
                      )}
                      {isRunning ? (
                        <div className="mt-1.5 space-y-1.5">
                          <p className="text-xs text-brand font-medium">
                            Pulling Google Ads data… <span className="text-text-tertiary">({elapsedMin} min)</span>
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleCancel(audit); }}
                              className="text-[11px] font-medium px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                              data-testid={`audit-cancel-btn-${audit.id}`}
                            >
                              Cancel & Remove
                            </button>
                            {elapsedMin >= 5 && (
                              <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                Taking longer than usual
                              </span>
                            )}
                          </div>
                        </div>
                      ) : isFailed ? (
                        <>
                          <p className="text-xs text-red-600 mt-1.5 line-clamp-2" title={audit.error}>{audit.error || `Audit ${audit.status?.toLowerCase() || 'failed'}`}</p>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDelete(audit.id); }}
                            className="text-[11px] font-medium px-2 py-1 mt-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                            data-testid={`audit-delete-failed-${audit.id}`}
                          >
                            Remove
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 mt-2 text-xs text-text-tertiary">
                            <span>{audit.percentage}%</span>
                            <span>{audit.total_score}/{audit.max_score} pts</span>
                            <span>{new Date(audit.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                            <div className={`h-full rounded-full ${gc.bg}`} style={{ width: `${audit.percentage}%` }} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PaidSearchAudit;
